import {
  createContext,
  FC,
  MutableRefObject,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { BackgroundData } from 'src/api/plan';
import { Floor } from 'src/api/types';
import {
  defaultDimensions,
  Dimensions,
  useOnResize,
} from 'src/hooks/useOnResize';
import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { Timeout } from 'src/types';

import { MAX_ZOOM, MIN_ZOOM, STANDARD_ZOOM } from './consts';
import { Point } from './types';

export interface FloorMapContextValue {
  floor: Floor | undefined;
  floorLoading: boolean;
  viewportRef: RefObject<HTMLDivElement>;
  zoomRef: MutableRefObject<number>;
  zoomValue: number;
  observeContainer: (node: HTMLDivElement | null) => void;
  containerDimensionsRef: MutableRefObject<Dimensions>;
  backgroundData: BackgroundData | undefined;
  scrollTo: (point: Point) => void;
  resizeFinished: () => Promise<void>;
  zoom: (value: number, point: Point) => void;
  isAnimating: boolean;
  backgroundOffset: Point;
  backgroundOffsetRef: MutableRefObject<Point>;
  setBackgroundOffset: (value: React.SetStateAction<Point>) => void;
}

export const FloorMapContext = createContext<FloorMapContextValue | undefined>(
  undefined,
);
FloorMapContext.displayName = 'FloorMapContext';

interface FloorMapProviderProps {
  children?: ReactNode;
  backgroundData: BackgroundData | undefined;
  floor: Floor | undefined;
  floorLoading?: boolean;
  offset?: Point;
}

const defaultOffset: Point = { x: 0, y: 0 };

export const FloorMapProvider: FC<FloorMapProviderProps> = ({
  children,
  backgroundData,
  floor,
  floorLoading = false,
  offset,
}) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const zoomRef = useRef(STANDARD_ZOOM);
  const [zoomValue, setZoomValue] = useState(STANDARD_ZOOM);
  const [isAnimating, setIsAnimating] = useState(false);
  const animatingTimeout = useRef<Timeout | null>();

  const getOffset = useCallback(() => {
    if (!viewportRef.current) return defaultOffset;

    if (!backgroundData) return defaultOffset;

    if (!offset?.x || !offset.y) return defaultOffset;

    const offsetX = (offset.x - backgroundData.width / 2) * zoomRef.current;
    const offsetY = (offset.y - backgroundData.height / 2) * zoomRef.current;

    const nextOffset = { x: offsetX, y: offsetY };

    return nextOffset;
  }, [backgroundData, offset?.x, offset?.y]);

  const [backgroundOffset, setBackgroundOffset] = useState(getOffset);
  const backgroundOffsetRef = useRef(backgroundOffset);

  useEffect(() => {
    const nextOffset = getOffset();
    setBackgroundOffset(nextOffset);
    backgroundOffsetRef.current = nextOffset;
  }, [getOffset]);

  const containerDimensionsRef = useRef(defaultDimensions);

  const handleResize = useCallback((value: Dimensions) => {
    containerDimensionsRef.current = value;
  }, []);

  const { observe: observeContainer, resizeFinished } =
    useOnResize(handleResize);

  const scrollTo = useCallback(
    (point: Point) => {
      if (!viewportRef.current) return;

      if (!backgroundData) return;

      setIsAnimating(true);
      if (animatingTimeout.current) clearTimeout(animatingTimeout.current);

      const offsetX = (point.x - backgroundData.width / 2) * zoomRef.current;
      const offsetY = (point.y - backgroundData.height / 2) * zoomRef.current;

      const nextOffset = { x: offsetX, y: offsetY };

      setBackgroundOffset(nextOffset);
      backgroundOffsetRef.current = nextOffset;

      animatingTimeout.current = setTimeout(() => {
        setIsAnimating(false);
        animatingTimeout.current = null;
      }, 500);
    },
    [backgroundData],
  );

  const zoom = useCallback((value: number, point: Point) => {
    if (!viewportRef.current) return;

    const offsetX = backgroundOffsetRef.current.x;
    const offsetY = backgroundOffsetRef.current.y;
    const adjustedPoint = {
      x: point.x - containerDimensionsRef.current.width / 2,
      y: point.y - containerDimensionsRef.current.height / 2,
    };
    const x = offsetX + adjustedPoint.x;
    const y = offsetY + adjustedPoint.y;

    const nextZoom = Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM);

    if (nextZoom === zoomRef.current) return;

    setIsAnimating(true);
    if (animatingTimeout.current) clearTimeout(animatingTimeout.current);

    const delta: Point = {
      x: (x * nextZoom) / zoomRef.current - x,
      y: (y * nextZoom) / zoomRef.current - y,
    };

    zoomRef.current = nextZoom;
    setZoomValue(nextZoom);
    const nextOffset = { x: offsetX + delta.x, y: offsetY + delta.y };
    setBackgroundOffset(nextOffset);
    backgroundOffsetRef.current = nextOffset;

    animatingTimeout.current = setTimeout(() => {
      setIsAnimating(false);
      animatingTimeout.current = null;
    }, 500);
  }, []);

  const contextValue = useMemo((): FloorMapContextValue => {
    return {
      floor,
      floorLoading,
      zoomRef,
      viewportRef,
      observeContainer,
      scrollTo,
      containerDimensionsRef,
      backgroundData,
      resizeFinished,
      zoom,
      zoomValue,
      backgroundOffset,
      setBackgroundOffset,
      backgroundOffsetRef,
      isAnimating,
    };
  }, [
    backgroundData,
    backgroundOffset,
    floor,
    floorLoading,
    isAnimating,
    observeContainer,
    resizeFinished,
    scrollTo,
    zoom,
    zoomValue,
  ]);

  useEffect(() => {
    zoomRef.current = STANDARD_ZOOM;
    setZoomValue(STANDARD_ZOOM);
    setBackgroundOffset(defaultOffset);
    backgroundOffsetRef.current = defaultOffset;
  }, [floor?.floorId]);

  return (
    <FloorMapContext.Provider value={contextValue}>
      {children}
    </FloorMapContext.Provider>
  );
};

export const useFloorMap = (): FloorMapContextValue =>
  useRequiredContext(FloorMapContext);
